<template>
  <property-item
    :name="property.name"
    :icon="getIconForType(property.type)"
    :label-style="{ marginTop: '1.4rem' }"
  >
    <c-form-field
      label=""
    >
      <c-select
        show-border
        overlapping
        value-required
        match-trigger-width
        :options="possiblePrefill"
        :value="[property.prefill]"
        @update:value="handlePrefillUpdate($event)"
      />
      <div
        v-if="errorMessage !== ''"
        :style="{ color: $colors.red.base, fontSize: $fontSizes[4], marginTop: '.4rem' }"
      >
        {{ errorMessage }}
        <a
          :href="`${websiteUrl}/app/plans`"
          :style="{ textDecoration: 'underline' }"
          target="_blank"
        >
          View plans.
        </a>
      </div>
    </c-form-field>

    <div v-if="valueToShow === 'predefinedSelection'">
      <c-form-field label="Select predefined option">
        <c-select
          show-border
          overlapping
          match-trigger-width
          :options="property.options"
          :value="property.predefined"
          @update:value="handlePredefinedUpdate($event)"
        />
      </c-form-field>
    </div>

    <div v-if="valueToShow === 'multiSelect'">
      <c-form-field label="Select predefined options">
        <c-select
          show-border
          overlapping
          match-trigger-width
          :options="property.options"
          multiple
          :value="property.predefined"
          @update:value="handlePredefinedUpdate($event)"
        />
      </c-form-field>
    </div>

    <div v-if="valueToShow === 'checkToggle'">
      <c-form-field label="Select predefined options">
        <c-focusable
          show-border
          @click="handleCheckboxUpdate"
        >
          <c-checkbox
            :value="property.checked"
            :style="{ margin: '0px 6px 0px 0px' }"
          />
        </c-focusable>
      </c-form-field>
    </div>

    <div v-if="valueToShow === 'predefinedText'">
      <c-form-field label="Write predefined value">
        <c-focusable show-border>
          <simple-text-editor
            :value="property.predefined[0]"
            :initial-content="property.predefined[0]"
            placeholder="Empty"
            return-string-value
            @update:value="handleTextUpdate"
          />
        </c-focusable>
      </c-form-field>
    </div>

    <div v-if="valueToShow === 'time'">
      <c-form-field label="Select relative value">
        <c-select
          :options="[
            { text: 'Now minus', value: -1, color: 'plain' },
            { text: 'Now plus', value: 1, color: 'plain' },
          ]"
          :value="[property.signTime]"
          value-required
          match-trigger-width
          show-border
          @update:value="handleTimeUpdate"
        />
        <div :style="{ display: 'flex', gap: '.8rem' }">
          <c-input
            :style="{ marginTop: '.6rem', width: '3rem' }"
            :value="property.numTime"
            show-border
            size="4"
            @update:value="handleNumTimeUpdate"
          />
          <c-select
            :style="{ marginTop: '.6rem', width: 'calc(100% - 3.8rem)' }"
            show-border
            :value="[property.unityTime]"
            match-trigger-width
            :options="[
              { text: 'day(s)', value: 'day', color: 'plain' },
              { text: 'month(s)', value: 'month', color: 'plain' },
              { text: 'year(s)', value: 'year', color: 'plain' },
            ]"
            @update:value="handleUnityTimeUpdate"
          />
        </div>
      </c-form-field>
    </div>

    <div v-if="valueToShow === 'predefinedDate'">
      <c-form-field label="Select a date">
        <c-date-picker
          block
          :show-border="true"
          :predefined="false"
          :value="predefinedDate"
          @update:value="handlePredefinedDateUpdate"
        />
      </c-form-field>
    </div>

    <div v-if="valueToShow === 'ai'">
      <c-form-field label="State your prompt">
        <c-focusable
          show-border
        >
          <div :style="{ display: 'block', width: '100%', padding: '.25rem 0' }">
            <simple-text-editor
              :value="property.prompt"
              :initial-content="property.prompt"
              placeholder="Enter your prompt..."
              disable-enter
              return-string-value
              @update:value="updatePrompt"
            />
          </div>
        </c-focusable>
      </c-form-field>
    </div>
  </property-item>
</template>

<script>
import CCheckbox from 'shared/components/CCheckbox.vue';
import CDatePicker from 'shared/components/CDatePicker.vue';
import CFocusable from 'shared/components/CFocusable.vue';
import CFormField from 'shared/components/CFormField.vue';
import CInput from 'shared/components/CInput.vue';
import CSelect from 'shared/components/CSelect.vue';
import PropertyItem from 'shared/components/PropertyItem.vue';
import SimpleTextEditor from 'shared/components/SimpleTextEditor.vue';
import useFeatureAccess from 'shared/services/feature-access';
import useSubscription from 'shared/services/subscription';
import {
  BETA_PHASE_MANAGER,
  USER_SERVICE,
  featureFlags,
} from 'shared/constants';
import { getIconForType } from 'shared/services/notion-icons';
import { inject } from 'vue';

export default {
  name: 'PropertyView',
  props: { propertyValue: { type: Object, required: true } },
  components: {
    PropertyItem,
    CFormField,
    CSelect,
    CInput,
    CCheckbox,
    CFocusable,
    CDatePicker,
    SimpleTextEditor,
  },
  data() {
    return { property: {} };
  },
  emits: ['update:value'],
  setup() {
    const userService = inject(USER_SERVICE);
    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const { currentPlan, subscriptionPastDue } = useSubscription(userService.currentUser, betaPhaseManager);
    const { hasFeatureAccess } = useFeatureAccess(currentPlan, subscriptionPastDue, betaPhaseManager);
    return { hasFeatureAccess };
  },
  computed: {
    filteredPrefillOptions() {
      return this.possiblePrefill;
    },
    websiteUrl() {
      return import.meta.env.VITE_WEBSITE_URL;
    },
    errorMessage() {
      if (!this.canUseAIProperties && this.property.prefill === 'ai') {
        return 'Upgrade to use this feature.';
      }
      return '';
    },
    canUseAIProperties() {
      return this.hasFeatureAccess(featureFlags.aiProperties);
    },
    possiblePrefill() {
      const gpt = { text: 'AI custom prefill', value: 'ai', icon: 'pro', color: 'plain' };
      const manual = { text: 'Empty', value: 'manual', icon: 'file', color: 'plain' };
      const predefined = { text: 'Predefined', value: 'predefined', icon: 'file-text', color: 'plain' };
      const url = { text: 'Url', value: 'url', icon: 'link', color: 'plain' };
      const pageTitle = { text: 'Page title', value: 'page title', icon: 'headline', color: 'plain' };
      if (
        ['select', 'multi_select', 'status', 'checkbox'].includes(
          this.property.type,
        )
      ) {
        return [
          manual,
          predefined,
          gpt,
        ];
      }
      if (this.property.type === 'date') {
        return [
          manual,
          predefined,
          { text: 'Relative dates', value: 'relative', color: 'plain', icon: 'calendar' },
          gpt,
        ];
      }
      if (['number', 'phone_number', 'email'].includes(this.property.type)) {
        return [
          manual,
          predefined,
          gpt,
        ];
      }
      if (['title', 'rich_text'].includes(this.property.type)) {
        return [
          manual,
          predefined,
          url,
          pageTitle,
          gpt,
        ];
      }
      if (this.property.type === 'url') {
        return [
          manual,
          predefined,
          url,
          gpt,
        ];
      }
      return [];
    },
    valueToShow() {
      let value = '';
      switch (this.property.prefill) {
        case 'manual':
          value = 'none';
          break;
        case 'url':
          value = 'none';
          break;
        case 'page title':
          value = 'none';
          break;
        case 'predefined':
          if (['select', 'status'].includes(this.property.type)) {
            value = 'predefinedSelection';
          } else if (['multi_select'].includes(this.property.type)) {
            value = 'multiSelect';
          } else if (['checkbox'].includes(this.property.type)) {
            value = 'checkToggle';
          } else if (['date'].includes(this.property.type)) {
            value = 'predefinedDate';
          } else {
            value = 'predefinedText';
          }
          break;
        case 'relative':
          value = 'time';
          break;
        case 'ai':
          value = 'ai';
          break;
        default:
      }
      return value;
    },
    predefinedDate() {
      return {
        start: this.property.predefined[0],
        end: null,
      };
    },
  },
  methods: {
    getIconForType,
    updatePrompt(val) {
      this.property.prompt = val;
      this.$emit('update:value', this.property);
    },
    handlePredefinedDateUpdate(value) {
      this.property.predefined = [value.start];
      this.$emit('update:value', this.property);
    },
    toggleEndDate() {
      this.property.endTime = !this.property.endTime;
      this.$emit('update:value', this.property);
    },
    handlePrefillUpdate([value]) {
      this.property.prefill = value;
      this.$emit('update:value', this.property);
      if (value === 'ai') {
        this.prefillPrompt();
      }
    },
    prefillPrompt() {
      if (this.property.prompt !== '' && this.property.prompt !== undefined) {
        return;
      }
      switch (this.property.type) {
        case 'select':
        case 'multi_select':
          this.updatePrompt('Based on the text, choose the most appropriate option.');
          break;
        case 'title':
        case 'rich_text':
          this.updatePrompt('Generate a compelling title for the text.');
          break;
        default:
      }
    },
    handlePredefinedUpdate(value) {
      this.property.predefined = value;
      this.$emit('update:value', this.property);
    },
    handleTextUpdate(value) {
      if (this.property.type === 'number') {
        value = parseFloat(value).toString();
        value = value === 'NaN' ? '' : value;
      }
      this.property.predefined = [value];
      this.$emit('update:value', this.property);
    },
    handleTimeUpdate([value]) {
      this.property.signTime = value;
      this.$emit('update:value', this.property);
    },
    handleNumTimeUpdate(value) {
      this.property.numTime = value;
      this.$emit('update:value', this.property);
    },
    handleUnityTimeUpdate([value]) {
      this.property.unityTime = value;
      this.$emit('update:value', this.property);
    },
    handleCheckboxUpdate() {
      this.property.checked = !this.property.checked;
      this.$emit('update:value', this.property);
    },
    handleInput(e, key) {
      if (key === 'checked') {
        this.property[key] = !this.property[key];
      } else if (key === 'predefinedText') {
        this.property.predefined = [e.target.value];
      } else if (key === 'predefined') {
        this.property[key] = [...e.target.selectedOptions].map(
          (opt) => opt.value,
        );
      } else {
        this.property[key] = e.target.value;
      }
      this.$emit('update:value', this.property);
    },
  },
  created() {
    this.property = this.propertyValue;
  },
};
</script>

<style scoped type="text/scss">
.todo {
  display: flex;
  flex-direction: column;
}
</style>
